import { Box, ButtonBase, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Typography } from "src/components-mui/base/typography/typography";
import { UnderlyingLogo } from "src/components-mui/common/underlying-logo/underlying-logo";
import {
    UNDERLYING_LIST_MOTIVE_CONFIG,
    UNDERLYING_LIST_MOTIVES,
    UnderlyingListMotive,
    getUnderlyingListMotiveColor,
} from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.constants";
import { UNDERLYING_LIST_SWITCH_BUTTON_TEST_ID } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.test-ids";
import { IUnderlyingLists } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.types";
import { themeShadows } from "src/theme-mui/tokens/shadows";

const TILE_LOGOS_COUNT = 3;

interface IListMotiveTileProps {
    motive: UnderlyingListMotive;
    isSelected: boolean;
    logoUrls: ReadonlyArray<string>;
    controlledListId: string;
    onSelect: (motive: UnderlyingListMotive) => void;
}

const ListMotiveTile = ({ motive, isSelected, logoUrls, controlledListId, onSelect }: IListMotiveTileProps) => {
    const { t } = useTranslation("underlying-overview");
    const { tileLabelKey } = UNDERLYING_LIST_MOTIVE_CONFIG[motive];
    const color = getUnderlyingListMotiveColor(motive);

    return (
        <ButtonBase
            aria-pressed={isSelected}
            aria-controls={controlledListId}
            onClick={() => onSelect(motive)}
            data-test-id={`${UNDERLYING_LIST_SWITCH_BUTTON_TEST_ID}-${motive}`}
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: { xs: "center", sm: "space-between" },
                gap: { xs: 1, sm: 0 },
                px: 2,
                pt: { xs: 1, sm: 2 },
                pb: 2,
                border: "2px solid",
                borderRadius: "8px",
                borderColor: isSelected ? `${color}.500` : `${color}.100`,
                backgroundColor: isSelected ? `${color}.100` : "common.white",
                color: "gray.800",
                textAlign: { xs: "center", sm: "left" },
                transition: "background-color 0.2s, border-color 0.2s",
                "&:hover": {
                    borderColor: `${color}.500`,
                },
                "&.Mui-focusVisible": {
                    boxShadow: themeShadows.focusVisible,
                },
            }}
        >
            <Typography component="span" sx={{ fontSize: { xs: "sm", sm: "md", md: "lg" }, fontWeight: "semibold" }}>
                {t(tileLabelKey)}
            </Typography>
            <Stack direction="row" sx={{ flexShrink: 0, "& > :not(:first-of-type)": { ml: { xs: -2, sm: -1 } } }}>
                {logoUrls.map(logoUrl => (
                    <UnderlyingLogo key={logoUrl} logoUrl={logoUrl} size="md" sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }} />
                ))}
            </Stack>
        </ButtonBase>
    );
};

interface IListMotiveTilesProps {
    lists: IUnderlyingLists;
    selectedMotive: UnderlyingListMotive;
    getListId: ({ motive }: { motive: UnderlyingListMotive }) => string;
    onSelect: (motive: UnderlyingListMotive) => void;
}

export const ListMotiveTiles = ({ lists, selectedMotive, getListId, onSelect }: IListMotiveTilesProps) => {
    const { t } = useTranslation("underlying-overview");

    return (
        <Box
            role="group"
            aria-label={t("trading-motives.aria-label")}
            sx={{
                display: { xs: "grid", md: "none" },
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
            }}
        >
            {UNDERLYING_LIST_MOTIVES.map(motive => (
                <ListMotiveTile
                    key={motive}
                    motive={motive}
                    isSelected={motive === selectedMotive}
                    logoUrls={lists[motive].items.slice(0, TILE_LOGOS_COUNT).map(({ logoUrl }) => logoUrl)}
                    controlledListId={getListId({ motive })}
                    onSelect={onSelect}
                />
            ))}
        </Box>
    );
};
